// src/admin/pages/VehicleDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { EmptyState } from "../ui/EmptyState";

// ---------- helpers ----------
function toneForOpsStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "available") return "success";
  if (s === "maintenance") return "danger";
  if (s === "booked") return "info";
  if (s === "cleaning") return "warning";
  if (s === "transit") return "neutral";
  if (s === "ops_hold") return "neutral";
  return "neutral";
}

function toneForBookingStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved") return "success";
  if (s === "requested") return "warning";
  if (s === "active") return "info";
  if (s === "cancelled") return "neutral";
  if (s === "completed") return "neutral";
  return "neutral";
}



function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  // inclusive overlap on date strings YYYY-MM-DD
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS <= bE && bS <= aE;
}

function KV({ k, v }) {
  return (
    <div className="kv__row">
      <div className="kv__k">{k}</div>
      <div className="kv__v">{v}</div>
    </div>
  );
}

function YesNo({ value }) {
  return value === null || value === undefined ? (
    <span className="muted">—</span>
  ) : value ? (
    <Badge tone="success">Yes</Badge>
  ) : (
    <Badge tone="neutral">No</Badge>
  );
}

// ---------- config: category -> detail table + fields ----------
const CATEGORY_DETAIL = {
  atv: {
    table: "vehicle_atv",
    title: "ATV specs",
    fields: [
      { k: "odometer_km", label: "Odometer (km)" },
      { k: "vin", label: "VIN" },
      { k: "engine_cc", label: "Engine (cc)" },
      { k: "drivetrain", label: "Drivetrain" },
      { k: "seats", label: "Seats" },
      { k: "weight_lb", label: "Weight (lb)" },
      { k: "power_steering", label: "Power steering", type: "bool" },
      { k: "winch", label: "Winch", type: "bool" },
    ],
  },

  sxs: {
    table: "vehicle_sxs",
    title: "Side-by-Side specs",
    fields: [
      { k: "odometer_km", label: "Odometer (km)" },
      { k: "vin", label: "VIN" },
      { k: "plate", label: "Plate" },
      { k: "engine_cc", label: "Engine (cc)" },
      { k: "drivetrain", label: "Drivetrain" },
      { k: "seats", label: "Seats" },
      { k: "weight_lb", label: "Weight (lb)" },
      { k: "winch", label: "Winch", type: "bool" },
      { k: "dump_box", label: "Dump box", type: "bool" },
    ],
  },

  snowmobile: {
    table: "vehicle_snowmobile",
    title: "Snowmobile specs",
    fields: [
      { k: "odometer_km", label: "Odometer (km)" },
      { k: "vin", label: "VIN" },
      { k: "engine_cc", label: "Engine (cc)" },
      { k: "engine_type", label: "Engine type" },
      { k: "turbo", label: "Turbo", type: "bool" },
      { k: "seats", label: "Seats" },
      { k: "plate", label: "Plate" },
      { k: "weight_lb", label: "Weight (lb)" },
      { k: "power_steering", label: "Power steering", type: "bool" },
      { k: "track_length_in", label: "Track length (in)" },
      { k: "heated_grips", label: "Heated grips", type: "bool" },
    ],
  },

  boat: {
    table: "vehicle_boat",
    title: "Boat specs",
    fields: [
      { k: "engine_hours", label: "Engine hours" },
      { k: "hin", label: "HIN" },
      { k: "boat_registration_number", label: "Boat registration #" },
      { k: "trailer_vin", label: "Trailer VIN" },
      { k: "trailer_plate", label: "Trailer plate" },
      { k: "weight_lb", label: "Weight (lb)" },
      { k: "livewell", label: "Livewell", type: "bool" },
      { k: "trolling_motor", label: "Trolling motor", type: "bool" },
      { k: "fish_finder_chartplotter", label: "Fish finder / chartplotter", type: "bool" },
      { k: "steering_type", label: "Steering type" },
      // keep your existing legacy columns too (optional context)
      { k: "length_ft", label: "Length (ft)" },
      { k: "engine_type", label: "Engine type (legacy)" },
      { k: "engine_hp", label: "Engine HP (legacy)" },
      { k: "capacity", label: "Capacity (legacy)" },
      { k: "fuel_type", label: "Fuel type (legacy)" },
      { k: "trailer_included", label: "Trailer included (legacy)", type: "bool" },
    ],
  },

  watercraft: {
    table: "vehicle_watercraft",
    title: "Watercraft (PWC) specs",
    fields: [
      { k: "engine_hours", label: "Engine hours" },
      { k: "hin", label: "HIN" },
      { k: "boat_registration_number", label: "Boat registration #" },
      { k: "trailer_vin", label: "Trailer VIN" },
      { k: "trailer_plate", label: "Trailer plate" },
      { k: "seats", label: "Seats" },
      { k: "horse_power", label: "Horse power" },
      { k: "engine_cc", label: "Engine (cc)" },
      { k: "supercharged", label: "Supercharged", type: "bool" },
      { k: "stereo", label: "Stereo", type: "bool" },
    ],
  },

  golf_cart: {
    table: "vehicle_golf_cart",
    title: "Golf cart specs",
    fields: [
      { k: "odometer_km", label: "Odometer (km)" },
      { k: "vin", label: "VIN" },
      { k: "plate", label: "Plate" },
      { k: "weight_lb", label: "Weight (lb)" },
      { k: "seats", label: "Seats" },
      { k: "engine_type", label: "Engine type" },
    ],
  },

  travel_trailer: {
    table: "vehicle_travel_trailer",
    title: "Travel trailer specs",
    fields: [
      { k: "vin", label: "VIN" },
      { k: "plate", label: "Plate" },
      { k: "gvwr_lb", label: "GVWR (lb)" },
      { k: "sleeps", label: "Sleeps" },
      { k: "length_ft", label: "Length (ft)" },
      { k: "dry_weight_lb", label: "Dry weight (lb)" },
      { k: "hitch_weight_lb", label: "Hitch weight (lb)" },
      { k: "slides", label: "Slides" },
      { k: "fresh_water_gal", label: "Fresh water (gal)" },
      { k: "grey_water_gal", label: "Grey water (gal)" },
      { k: "black_water_gal", label: "Black water (gal)" },
    ],
  },
};

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const nav = useNavigate();

  const [vehicle, setVehicle] = useState(null); // base vehicles row
  const [detail, setDetail] = useState(null); // category-specific row
  const [bookings, setBookings] = useState([]); // bookings for this vehicle
  const [bookingMembers, setBookingMembers] = useState(new Map()); // membership_id -> {membership, contact}
  const [approvedActiveBookings, setApprovedActiveBookings] = useState([]); // for "available now"
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const cfg = useMemo(() => {
    const cat = String(vehicle?.category || "").toLowerCase();
    return CATEGORY_DETAIL[cat] || null;
  }, [vehicle?.category]);

  // compute availability from DB bookings + ops status
  const availability = useMemo(() => {
    if (!vehicle) return null;
    const ops = String(vehicle.status || "").toLowerCase();
    if (ops !== "available") return { isAvailableNow: false, reason: `Ops status: ${vehicle.status}` };

    // if any approved/active booking overlaps today => not available now
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const todayISO = `${y}-${m}-${d}`;

    const hasOverlap = (approvedActiveBookings || []).some((b) =>
      overlaps(todayISO, todayISO, b.start_date, b.end_date)
    );

    return hasOverlap
      ? { isAvailableNow: false, reason: "Booked (approved/active overlap today)" }
      : { isAvailableNow: true, reason: "Clear" };
  }, [vehicle, approvedActiveBookings]);

  // load everything
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr(null);

      try {
        // 1) base vehicle
        const { data: v, error: vErr } = await supabase
          .from("vehicles")
          .select(
            `
            id,
            category,
            name,
            make,
            model,
            year,
            trim,
            status,
            location,
            tier_access,
            card_tag,
            card_desc,
            sub_category,
            created_at,
            updated_at
          `
          )
          .eq("id", vehicleId)
          .single();

        if (vErr) throw vErr;

        // 2) detail row (based on category)
        const cat = String(v.category || "").toLowerCase();
        const cfgLocal = CATEGORY_DETAIL[cat] || null;

        let detailRow = null;
        if (cfgLocal?.table) {
          const { data: dRow, error: dErr } = await supabase
            .from(cfgLocal.table)
            .select("*")
            .eq("vehicle_id", v.id)
            .maybeSingle();

          // If you haven’t created the detail row yet, maybeSingle() returns null without error
          if (dErr) throw dErr;
          detailRow = dRow || null;
        }

        // 3) bookings for this vehicle
        const { data: bks, error: bErr } = await supabase
          .from("bookings")
          .select("id, membership_id, vehicle_id, start_date, end_date, status, pickup_location, dropoff_location, concierge, notes, created_at")
          .eq("vehicle_id", v.id)
          .order("created_at", { ascending: false });

        if (bErr) throw bErr;

        // 4) approved/active bookings for availability calc (recent is fine)
        const { data: aa, error: aaErr } = await supabase
          .from("bookings")
          .select("id, vehicle_id, start_date, end_date, status")
          .eq("vehicle_id", v.id)
          .in("status", ["approved", "active"])
          .order("created_at", { ascending: false })
          .limit(500);

        if (aaErr) throw aaErr;

        // 5) member lookup for bookings list (memberships + contact)
        const membershipIds = Array.from(new Set((bks || []).map((b) => b.membership_id).filter(Boolean)));
        let memberMap = new Map();

        if (membershipIds.length) {
          const { data: mems, error: mErr } = await supabase
            .from("memberships")
            .select(
              `
              id,
              status,
              tier,
              contact_id,
              crm_contacts:contact_id (
                id,
                first_name,
                last_name,
                email,
                phone
              )
            `
            )
            .in("id", membershipIds);

          if (mErr) throw mErr;

          (mems || []).forEach((mrow) => {
            memberMap.set(mrow.id, { membership: mrow, contact: mrow.crm_contacts || null });
          });
        }

        if (!cancelled) {
          setVehicle(v);
          setDetail(detailRow);
          setBookings(bks || []);
          setApprovedActiveBookings(aa || []);
          setBookingMembers(memberMap);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load vehicle.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [vehicleId]);

  if (loading) {
    return (
      <div className="page">
        <div className="page__head">
          <div className="page__title">Vehicle</div>
          <div className="page__sub">Loading from Supabase…</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="page">
        <div className="page__head page__head--row">
          <div>
            <div className="page__back">
              <Link className="link" to="/admin/vehicles">← Vehicles</Link>
            </div>
            <h1 className="page__title">Vehicle</h1>
            <div className="page__sub">Could not load this vehicle.</div>
          </div>
        </div>

        <div className="adminAuth__error" style={{ marginTop: 12 }}>{err}</div>
        <EmptyState title="Vehicle not found" hint="Check the URL or go back to Vehicles." />
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="page">
        <EmptyState title="Vehicle not found" hint="Check the ID or go back to Vehicles." />
      </div>
    );
  }

  const cat = String(vehicle.category || "").toLowerCase();
  const title = CATEGORY_DETAIL[cat]?.title || "Vehicle specs";

  return (
    <div className="page">
      <div className="page__head">
        <div className="page__back">
          <Link className="link" to="/admin/vehicles">← Vehicles</Link>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 className="page__title">{vehicle.name}</h1>
            <div className="page__sub">
              {vehicle.category} • {vehicle.year || "—"} • {vehicle.location || "—"}
              {vehicle.sub_category ? ` • ${vehicle.sub_category}` : ""}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Badge tone={toneForOpsStatus(vehicle.status)}>{vehicle.status}</Badge>
            <button className="btn btn--ghost" type="button" onClick={() => nav("/admin/vehicles")}>
              Back to list
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid--2">
        <Card
          title="Status"
          right={
            availability?.isAvailableNow ? (
              <Badge tone="success">Available now</Badge>
            ) : (
              <Badge tone="danger">Not available</Badge>
            )
          }
        >
          <div className="kv">
            <KV
              k="Available now"
              v={
                availability?.isAvailableNow ? (
                  <Badge tone="success">Yes</Badge>
                ) : (
                  <Badge tone="danger">No • {availability?.reason || "—"}</Badge>
                )
              }
            />
            <KV k="Ops status" v={<Badge tone={toneForOpsStatus(vehicle.status)}>{vehicle.status}</Badge>} />
            <KV k="Tier access" v={<span className="muted">{(vehicle.tier_access || []).join(", ")}</span>} />
            <KV k="Make" v={<span className="muted">{vehicle.make || "—"}</span>} />
            <KV k="Model" v={<span className="muted">{vehicle.model || "—"}</span>} />
            <KV k="Trim" v={<span className="muted">{vehicle.trim || "—"}</span>} />
            <KV k="Sub category" v={<span className="muted">{vehicle.sub_category || "—"}</span>} />
            <KV k="Card tag" v={<span className="muted">{vehicle.card_tag || "—"}</span>} />
            <KV k="Card desc" v={<span className="muted">{vehicle.card_desc || "—"}</span>} />
          </div>
        </Card>

        <Card title={title} subtitle={cfg?.table ? `Saved in ${cfg.table}` : "No detail table for this category"}>
          {!cfg?.table ? (
            <div className="muted">No category-specific table exists for this category.</div>
          ) : !detail ? (
            <div className="muted">
              No detail row found in <b>{cfg.table}</b> for this vehicle yet.
              <div style={{ marginTop: 8 }}>
                Tip: when creating a vehicle, your NewVehicle page should insert <b>vehicles</b> + this table row.
              </div>
            </div>
          ) : (
            <div className="kv">
              {cfg.fields.map((f) => {
                const raw = detail?.[f.k];
                const v =
                  f.type === "bool" ? (
                    <YesNo value={raw} />
                  ) : (
                    <span className="muted">{raw === null || raw === undefined || raw === "" ? "—" : String(raw)}</span>
                  );
                return <KV key={f.k} k={f.label} v={v} />;
              })}
            </div>
          )}
        </Card>
      </div>

      <Card title="Bookings for this vehicle" subtitle="Requested / approved / active / history">
        <div className="stack">
          {bookings.length ? (
            bookings.map((b) => {
              const bundle = bookingMembers.get(b.membership_id);
              const contact = bundle?.contact || null;
              const name = contact ? fullNameFromContact(contact) : String(b.membership_id || "").slice(0, 8);

              return (
                <div key={b.id} className="bookingRow">
                  <div className="bookingRow__main">
                    <div className="bookingRow__top">
                      <div className="bookingRow__id">{String(b.id).slice(0, 8)}</div>
                      <Badge tone={toneForBookingStatus(b.status)}>{b.status}</Badge>
                    </div>

                    <div className="bookingRow__sub">
                      {b.start_date} → {b.end_date} • Member:{" "}
                      <Link className="link" to={`/admin/members/${b.membership_id}`}>
                        {name}
                      </Link>
                      {" "}• Concierge: <span className="muted">{b.concierge || "—"}</span>
                    </div>

                    <div className="muted" style={{ marginTop: 6 }}>
                      Pickup: {b.pickup_location || "—"} • Drop-off: {b.dropoff_location || "—"}
                    </div>

                    {b.notes ? <div className="muted" style={{ marginTop: 6 }}>{b.notes}</div> : null}
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState title="No bookings yet" hint="This will populate as bookings are created." />
          )}
        </div>
      </Card>
    </div>
  );
}